import React, { useEffect, useRef } from 'react';
import { DrawerTitle } from '@components/ui/drawer';
import type { SelfReportedRegistration } from '@models/api';
import type { RegistrationCelebration } from '@utils/registrationCelebration';
import { PinChip, SheetBody, SheetFooter } from './EventPinCard';
import { ticketQuestion } from './eventPinCopy';
import { TicketQuestionAnswers, TicketQuestionText, TicketSite } from './TicketQuestionContent';
import { useTicketQuestion } from './useTicketQuestion';
import type { Chip } from './useEventPinSteps';

interface EventPinTicketStepProps {
  registration: RegistrationCelebration;
  source: string | null;
  /** The host's answer handler (CelebrationHost `answer`): it saves and closes. */
  onAnswer: (value: SelfReportedRegistration) => boolean;
  chip: Chip | null;
  motionClass: string | undefined;
}

/**
 * The last step of the Going PIN sheet: the ticket question, after the PIN cards. Its own component
 * so `useTicketQuestion` mounts (and reports the question as shown) only when this step shows.
 * Going is already confirmed by the cards before it, so its frame is the question alone: no
 * eyebrow, no illustration, no footnote, and `ticketQuestion` is the title.
 */
export const EventPinTicketStep: React.FC<EventPinTicketStepProps> = ({
  registration,
  source,
  onAnswer,
  chip,
  motionClass,
}) => {
  const question = useTicketQuestion({ registration, source, onAnswer });
  // The step replaces the card under the reader: focus its title so the question is announced.
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  return (
    <>
      <SheetBody animationKey="ticket" motionClass={motionClass}>
        <div className="flex flex-col gap-4 px-5 pt-4 pb-2">
          {chip ? <PinChip chip={chip} /> : null}
          <div className="flex flex-col items-center gap-2.5 pt-2 text-center">
            <DrawerTitle
              ref={titleRef}
              tabIndex={-1}
              className="font-livvic text-2xl font-bold text-black outline-none"
            >
              {ticketQuestion}
            </DrawerTitle>
            {/* Under "Got your ticket?", where the event is ticketed: not "Get your ticket on…",
                which answers the title's question for the reader. */}
            <TicketQuestionText
              question={question}
              upcomingLead={
                <>
                  This event is ticketed on{' '}
                  <TicketSite domain={question.domain} url={registration.registration_url} />.
                </>
              }
            />
          </div>
        </div>
      </SheetBody>
      <SheetFooter>
        <TicketQuestionAnswers question={question} />
      </SheetFooter>
    </>
  );
};
